import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: { full_name?: string; country?: string; user_status?: string; institution?: string }) => Promise<{ error: Error | null }>;
  signIn: (email: string, password: string) => Promise<{ error: Error | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;
    let authEventReceived = false;
    let lastEnrichedSessionId: string | null = null;
    let lastWelcomeCheckedUserId: string | null = null;

    const applySession = (nextSession: Session | null) => {
      if (!isMounted) return;
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      setLoading(false);
    };

    const ensureWelcomeNotification = async (currentUserId?: string) => {
      if (!currentUserId || lastWelcomeCheckedUserId === currentUserId) return;
      lastWelcomeCheckedUserId = currentUserId;

      // Check permanent flag on profile — guarantees welcome is only ever sent once per account
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('welcome_sent_at')
        .eq('user_id', currentUserId)
        .maybeSingle();

      if (profileError || !profile || profile.welcome_sent_at) return;

      let welcomeTitle = 'Welcome to Refify! 🎉';
      let welcomeMessage = 'Your workspace is ready. You can now create papers, organise references, and share your work with others.';

      const { data: template } = await supabase
        .from('notification_templates')
        .select('title, message, is_active')
        .eq('type', 'welcome')
        .maybeSingle();

      if (template) {
        if (!template.is_active) return;
        welcomeTitle = template.title;
        welcomeMessage = template.message;
      }

      const { error: insertError } = await supabase.from('notifications').insert({
        user_id: currentUserId,
        type: 'welcome',
        title: welcomeTitle,
        message: welcomeMessage,
        is_read: false,
      });

      // Only mark as sent if the notification was actually created
      if (!insertError) {
        await supabase
          .from('profiles')
          .update({ welcome_sent_at: new Date().toISOString() })
          .eq('user_id', currentUserId);
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, nextSession) => {
        authEventReceived = true;
        applySession(nextSession);

        if (nextSession?.user) {
          void ensureWelcomeNotification(nextSession.user.id);
        }

        if (event === 'SIGNED_IN' && nextSession && nextSession.access_token !== lastEnrichedSessionId) {
          lastEnrichedSessionId = nextSession.access_token;

          supabase.functions.invoke('enrich-geolocation', {
            body: { user_id: nextSession.user.id }
          });
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
      if (!isMounted || authEventReceived) return;

      applySession(currentSession);

      if (currentSession?.user) {
        void ensureWelcomeNotification(currentSession.user.id);
      }
    });

    return () => {
      isMounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (
    email: string,
    password: string,
    metadata?: { full_name?: string; country?: string; user_status?: string; institution?: string }
  ) => {
    const redirectUrl = `${window.location.origin}/auth/callback`;

    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
      }
    });
    return { error: error as Error | null };
  };

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password
    });
    return { error: error as Error | null };
  };

  const signOut = async () => {
    setUser(null);
    setSession(null);
    setLoading(false);

    const { error } = await supabase.auth.signOut({ scope: 'local' });
    if (error) {
      throw error as Error;
    }
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
